import uniqBy from 'lodash/uniqBy';
import type { Cart } from 'types/cart/Cart';
import type { DiscountAllocation } from 'types/cart/DiscountAllocation';

/**
 * @description Merge the discount allocations based on their code. If a discount allocation with the same code already exists in the accumulator array, the function adds the discounted amount of the current allocation to the existing one. If not, it adds the current allocation to the accumulator array.
 */
export const mergeCartDiscountAllocations = ({
  discountAllocations,
}: {
  discountAllocations: DiscountAllocation[];
}) =>
  discountAllocations?.reduce(
    (acc: DiscountAllocation[], curr: DiscountAllocation) => {
      const index = acc.findIndex((item) => item.code === curr.code);

      if (index === -1) {
        acc.push(curr);
      } else {
        acc[index].discountedAmount.amount =
          Number(acc[index].discountedAmount.amount) +
          Number(curr.discountedAmount.amount);
      }
      return acc;
    },
    [],
  );

/**
 * @description The discount code is converted to uppercase to ensure that the discount code is not duplicated when the user enters a discount code in uppercase. The discountCodes are then deduped using the code to make sure we only render them once.
 */
export const cleanDiscountCodes = ({
  discountCodes,
}: {
  discountCodes: Cart['discountCodes'];
}) =>
  uniqBy(
    discountCodes.map((dC) => ({
      ...dC,
      code: dC.code.toUpperCase(),
    })),
    'code',
  );

export const getCartTotalDiscountAmount = ({
  discountAllocations,
}: {
  discountAllocations: DiscountAllocation[];
}) =>
  discountAllocations?.reduce(
    (acc: number, curr: DiscountAllocation) =>
      acc + Number(curr.discountedAmount.amount),
    0,
  );
